import { makeAutoObservable } from 'mobx'
import DebouncedLoadingState from './DebouncedLoadingState'

export default class Loader {
   count = 0
   /** Indicate wether this Loader has ever been loaded */
   public hasLoadedAtleastOnce = false
   private loading: DebouncedLoadingState
   private _loaderText: string | undefined = undefined
   private loadingPromise: Promise<void> | null = null
   private loadingPromiseResolve: (() => void) | null = null
   private parentLoader?: Loader

   constructor(initialValue?: boolean, options?: { parentLoader?: Loader }) {
      this.loading = new DebouncedLoadingState({ initial: initialValue, debounce: 0 })
      this.parentLoader = options?.parentLoader
      makeAutoObservable(this)
   }

   get isLoading() {
      return this.loading.isLoading
   }

   get loaderText() {
      return this._loaderText
   }

   get promise(): Promise<void> {
      if (this.count === 0) {
         // If not loading, return resolved promise
         return Promise.resolve()
      }
      // If loading, return the current loading promise
      return this.loadingPromise || Promise.resolve()
   }

   createChildLoader = (initialValue?: boolean) => {
      return new Loader(initialValue, { parentLoader: this })
   }

   executeLoading = async <R>(action: () => Promise<R>, loaderText?: string): Promise<R> => {
      if (this.parentLoader) {
         return await this.parentLoader.executeLoading(async () => {
            return await this._executeLoadingInternal(action, loaderText)
         }, loaderText)
      }
      return await this._executeLoadingInternal(action, loaderText)
   }

   private _executeLoadingInternal = async <R>(action: () => Promise<R>, loaderText?: string): Promise<R> => {
      // Always (re)set, so a call without its own text can't inherit a previous operation's label.
      this._loaderText = loaderText
      let ret: R
      this.count++

      if (this.count === 1) {
         this.loadingPromise = new Promise<void>((resolve) => {
            this.loadingPromiseResolve = resolve
         })
         this.loading.set()
      }

      try {
         ret = await action()
      } finally {
         // this is executed even when an error is re-thrown
         this.count--
         if (this.count === 0) {
            this.loading.unset()
            if (this.loadingPromiseResolve) {
               this.loadingPromiseResolve()
               this.loadingPromiseResolve = null
               this.loadingPromise = null
            }
         }
      }

      this.hasLoadedAtleastOnce = true
      return ret
   }
}
